/**
 * Compliance Tracker
 * ------------------------------------------------------------
 * Team-member and admin view of compliance OCCURRENCES.
 *
 * View toggles:
 *   • List — filterable table (fund, section, WIP, compliance status,
 *     month, assignee) + column picker (add Description / Remarks /
 *     Completion Date / Section / Type / custom fields)
 *   • Calendar — month-wise grid with due-date pills
 *
 * Scope toggle:
 *   • Mine (default for non-admin) — only occurrences assigned to caller
 *   • All (admin only)             — every occurrence in the AMC
 *
 * All dates render in DD-MMMM-YYYY via the shared useDateFormat() hook.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, List as ListIcon, Filter, Database, ChevronLeft, ChevronRight, Columns, FolderPlus, X, CalendarPlus, BarChart3, Mail, UserCog, Zap } from 'lucide-react';
import { complianceAPI, fundAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormat } from '../hooks/useDateFormat';
import OccurrenceDrawer from '../components/compliance/OccurrenceDrawer';
import AddEventModal from '../components/compliance/AddEventModal';
import BulkReassignModal from '../components/compliance/BulkReassignModal';
import AdhocTaskModal from '../components/compliance/AdhocTaskModal';

const STATUS_BADGE = {
  'Complied': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Not Complied': 'bg-red-100 text-red-800 border-red-200',
  'On Track': 'bg-blue-100 text-blue-800 border-blue-200',
  'Not Applicable': 'bg-gray-200 text-gray-600 border-gray-300',
};
const WIP_BADGE = {
  'Not Started': 'bg-gray-100 text-gray-700 border-gray-200',
  'Active': 'bg-amber-100 text-amber-800 border-amber-200',
  'Completed': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Not Applicable': 'bg-gray-200 text-gray-600 border-gray-300',
};

const monthKey = (isoDate) => (isoDate || '').slice(0, 7);
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const pad2 = n => String(n).padStart(2, '0');

const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || '').trim();
};

// Always-on base columns. Optional extras are toggled via the column picker.
const OPTIONAL_COLUMNS = [
  { key: 'section', label: 'Section' },
  { key: 'type', label: 'Type' },
  { key: 'description', label: 'Description' },
  { key: 'remarks', label: 'Remarks' },
  { key: 'completion_date', label: 'Completion Date' },
];

const LS_KEY = 'compliance_tracker_columns';

const ComplianceTracker = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fmtDate = useDateFormat();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';

  const [occurrences, setOccurrences] = useState([]);
  const [sections, setSections] = useState([]);
  const [funds, setFunds] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list');
  const [scope, setScope] = useState(isAdmin ? 'all' : 'mine');

  // Filters
  const [filters, setFilters] = useState({ fund_id: '', section_id: '', status: '', compliance_status: '', compliance_type: '', assignee_id: '', month: '' });
  const [drawer, setDrawer] = useState(null);
  const [showColPicker, setShowColPicker] = useState(false);
  const [showFundPicker, setShowFundPicker] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showBulkReassign, setShowBulkReassign] = useState(false);
  const [showAdhoc, setShowAdhoc] = useState(false);

  // Optional columns (persisted). Custom-field keys discovered at runtime
  // are appended dynamically so admins can show those too.
  const [extraCols, setExtraCols] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY)) || []; } catch { return []; }
  });

  // Bulk-assign selection state
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignee, setBulkAssignee] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  // Reassignment inbox
  const [reassignments, setReassignments] = useState([]);
  // Calendar cursor
  const today = new Date();
  const [calCursor, setCalCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const toggleSelected = (id) => {
    setSelected(s => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(s => {
      if (s.size === occurrences.length) return new Set();
      return new Set(occurrences.map(o => o.id));
    });
  };
  const applyBulkAssign = async () => {
    if (selected.size === 0) return;
    setBulkSaving(true);
    try {
      await complianceAPI.bulkAssign(Array.from(selected), bulkAssignee || null);
      setSelected(new Set());
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Bulk-assign failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const fetchReassignments = async () => {
    try {
      const r = await complianceAPI.listReassignments();
      setReassignments(r.data || []);
    } catch { /* ignore */ }
  };

  const claimOccurrence = async (occId) => {
    try {
      await complianceAPI.claimOccurrence(occId);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to self-assign');
    }
  };

  const decideReassign = async (reqId, decision, note) => {
    try {
      await complianceAPI.decideReassignment(reqId, { decision, note });
      await Promise.all([fetchReassignments(), fetchAll()]);
    } catch (err) {
      alert(err.response?.data?.detail || 'Decision failed');
    }
  };

  const customFieldCols = useMemo(() => {
    const seen = new Map();
    occurrences.forEach(o => {
      (o.custom_fields || []).forEach(cf => {
        if (cf.key && cf.label && !seen.has(cf.key)) seen.set(cf.key, cf.label);
      });
    });
    return Array.from(seen, ([key, label]) => ({ key: `cf:${key}`, label }));
  }, [occurrences]);

  const allOptional = [...OPTIONAL_COLUMNS, ...customFieldCols];

  const toggleCol = (key) => {
    setExtraCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
  };

  const fetchAll = async () => {
    setLoading(true);
    const params = { scope };
    if (filters.fund_id) params.fund_id = filters.fund_id;
    if (filters.section_id) params.section_id = filters.section_id;
    if (filters.status) params.status = filters.status;
    if (filters.compliance_status) params.compliance_status = filters.compliance_status;
    if (filters.compliance_type) params.compliance_type = filters.compliance_type;
    if (filters.assignee_id) params.assignee_id = filters.assignee_id;
    if (filters.month) {
      params.from_date = `${filters.month}-01`;
      const [y, m] = filters.month.split('-').map(Number);
      params.to_date = `${filters.month}-${pad2(daysInMonth(y, m - 1))}`;
    }
    try {
      const [occRes, secRes, fundRes, teamRes] = await Promise.all([
        complianceAPI.listOccurrences(params),
        complianceAPI.listSections(),
        fundAPI.getAll(),
        complianceAPI.listTeamMembers(),
      ]);
      setOccurrences(occRes.data || []);
      setSections(secRes.data || []);
      setFunds(fundRes.data || []);
      setTeam(teamRes.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [scope, filters]);
  useEffect(() => { fetchReassignments(); }, []);

  const byMonth = useMemo(() => {
    const map = {};
    occurrences.forEach(o => {
      const k = monthKey(o.due_date);
      if (!k) return;
      (map[k] = map[k] || []).push(o);
    });
    return map;
  }, [occurrences]);

  const calMonthKey = `${calCursor.y}-${pad2(calCursor.m + 1)}`;
  const calItems = byMonth[calMonthKey] || [];

  const shiftMonth = (delta) => {
    setCalCursor(c => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  };

  const renderExtraCell = (o, key) => {
    switch (key) {
      case 'section': return o.section_name || '—';
      case 'type': return (o.compliance_type || '').replace('_', '-') || '—';
      case 'description': return <span className="text-gray-600">{truncate(stripHtml(o.item_description_html), 120) || '—'}</span>;
      case 'remarks': return <span className="text-gray-600">{truncate(stripHtml(o.remarks_html), 120) || '—'}</span>;
      case 'completion_date': return o.completion_date ? <span className="font-mono">{fmtDate(o.completion_date)}</span> : '—';
      default: {
        if (key.startsWith('cf:')) {
          const cfKey = key.slice(3);
          return (o.custom_values || {})[cfKey] ?? '—';
        }
        return '—';
      }
    }
  };

  return (
    <div className="p-6 max-w-[110rem] mx-auto" data-testid="compliance-tracker-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Compliance Tracker</h1>
          <p className="text-gray-600 mt-1">
            {isAdmin
              ? 'All compliance line items across your AMC. Toggle "Mine" to filter to yours.'
              : 'Compliance line items assigned to you. Switch to "Unassigned" to pick up items nobody has claimed yet.'}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/compliance/database"
                className="inline-flex items-center px-3 py-1.5 text-sm border rounded-lg text-gray-700 hover:bg-gray-50"
                data-testid="go-to-database">
            <Database size={14} className="mr-1.5" /> {isAdmin ? 'Manage Database' : 'Compliance Database'}
          </Link>
        </div>
      </div>

      {/* Controls row */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border overflow-hidden">
            {isAdmin && (
              <button onClick={() => setScope('all')}
                      className={`px-3 py-1.5 text-sm ${scope === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                      data-testid="scope-all">All</button>
            )}
            <button onClick={() => setScope('mine')}
                    className={`px-3 py-1.5 text-sm ${isAdmin ? 'border-l' : ''} ${scope === 'mine' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="scope-mine">Mine</button>
            <button onClick={() => setScope('unassigned')}
                    className={`px-3 py-1.5 text-sm border-l ${scope === 'unassigned' ? 'bg-red-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="scope-unassigned"
                    title="Items nobody has picked up yet — you can assign them to yourself">
              Unassigned
            </button>
          </div>

          <div className="inline-flex rounded-lg border overflow-hidden">
            <button onClick={() => setView('list')}
                    className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 ${view === 'list' ? 'bg-gray-800 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="view-list">
              <ListIcon size={14} /> List
            </button>
            <button onClick={() => setView('calendar')}
                    className={`px-3 py-1.5 text-sm inline-flex items-center gap-1 border-l ${view === 'calendar' ? 'bg-gray-800 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
                    data-testid="view-calendar">
              <CalendarIcon size={14} /> Calendar
            </button>
          </div>

          {/* Action buttons — compact, next to view toggles */}
          <button
            onClick={() => setShowAddEvent(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            data-testid="add-event-btn"
            title="Record an event for an event-based compliance"
          >
            <CalendarPlus size={14} className="mr-1.5" /> Add Event
          </button>
          <button
            onClick={() => setShowAdhoc(true)}
            className="inline-flex items-center px-3 py-1.5 text-sm border border-amber-600 text-amber-700 rounded-lg hover:bg-amber-50"
            data-testid="add-adhoc-btn"
            title="Create a fund-specific adhoc compliance task (won't appear in the Compliance Database)"
          >
            <Zap size={14} className="mr-1.5" /> Add Task / Compliance Item
          </button>
          <Link to="/compliance/reports"
                className="inline-flex items-center px-3 py-1.5 text-sm border border-indigo-600 text-indigo-700 rounded-lg hover:bg-indigo-50"
                data-testid="go-to-reports">
            <BarChart3 size={14} className="mr-1.5" /> Reports
          </Link>
          {isAdmin && (
            <Link to="/compliance/email-triggers"
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-violet-600 text-violet-700 rounded-lg hover:bg-violet-50"
                  data-testid="go-to-email-triggers">
              <Mail size={14} className="mr-1.5" /> Email Triggers
            </Link>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowFundPicker(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-emerald-600 text-emerald-700 rounded-lg hover:bg-emerald-50"
              data-testid="assign-funds-btn"
            >
              <FolderPlus size={14} className="mr-1.5" /> Assign to Fund
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setShowBulkReassign(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm border border-orange-600 text-orange-700 rounded-lg hover:bg-orange-50"
              data-testid="bulk-reassign-btn"
              title="Transfer every pending compliance from one team member to another"
            >
              <UserCog size={14} className="mr-1.5" /> Bulk Reassign
            </button>
          )}

          <div className="flex flex-wrap items-center gap-2 ml-auto">
            <Filter size={14} className="text-gray-500" />
            <select value={filters.fund_id} onChange={e => setFilters(f => ({ ...f, fund_id: e.target.value }))}
                    className="text-sm border rounded px-2 py-1" data-testid="filter-fund">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select value={filters.section_id} onChange={e => setFilters(f => ({ ...f, section_id: e.target.value }))}
                    className="text-sm border rounded px-2 py-1" data-testid="filter-section">
              <option value="">All Sections</option>
              {sections.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                    className="text-sm border rounded px-2 py-1" data-testid="filter-status">
              <option value="">All WIP</option>
              <option>Not Started</option>
              <option>Active</option>
              <option>Completed</option>
              <option>Not Applicable</option>
            </select>
            <select value={filters.compliance_status} onChange={e => setFilters(f => ({ ...f, compliance_status: e.target.value }))}
                    className="text-sm border rounded px-2 py-1" data-testid="filter-compliance-status">
              <option value="">All Status</option>
              <option>Complied</option>
              <option>Not Complied</option>
              <option>On Track</option>
              <option>Not Applicable</option>
            </select>
            <select value={filters.compliance_type} onChange={e => setFilters(f => ({ ...f, compliance_type: e.target.value }))}
                    className="text-sm border rounded px-2 py-1" data-testid="filter-compliance-type">
              <option value="">All Types</option>
              <option value="recurring">Recurring</option>
              <option value="event_based">Event-based</option>
              <option value="one_time">One-time</option>
            </select>
            {isAdmin && (
              <select value={filters.assignee_id} onChange={e => setFilters(f => ({ ...f, assignee_id: e.target.value }))}
                      className="text-sm border rounded px-2 py-1" data-testid="filter-assignee">
                <option value="">All Assignees</option>
                <option value="unassigned">Unassigned</option>
                {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
            <input type="month" value={filters.month} onChange={e => setFilters(f => ({ ...f, month: e.target.value }))}
                   className="text-sm border rounded px-2 py-1" data-testid="filter-month" />

            {/* Column picker */}
            <div className="relative">
              <button onClick={() => setShowColPicker(s => !s)}
                      className="p-1.5 border rounded text-gray-600 hover:bg-gray-50"
                      title="Choose columns"
                      data-testid="column-picker-btn">
                <Columns size={16} />
              </button>
              {showColPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowColPicker(false)} />
                  <div className="absolute right-0 mt-1 w-64 bg-white border rounded-lg shadow-lg z-20 p-3" data-testid="column-picker-menu">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-700">Add columns</span>
                      <button onClick={() => setShowColPicker(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                    </div>
                    <p className="text-[11px] text-gray-500 mb-2">Due Date, Internal Due, Compliance, Fund, Assignee, WIP and Status are always shown.</p>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                      {allOptional.map(col => (
                        <label key={col.key} className="flex items-center gap-2 text-sm py-0.5 cursor-pointer" data-testid={`col-toggle-${col.key}`}>
                          <input type="checkbox" checked={extraCols.includes(col.key)} onChange={() => toggleCol(col.key)} />
                          <span>{col.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500 mb-3">Loading…</div>}

      {/* Reassignment banner */}
      {reassignments.length > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-lg mb-4" data-testid="reassign-banner">
          <div className="px-4 py-2 border-b border-violet-200 text-xs font-semibold text-violet-800 uppercase tracking-wide">
            Reassignment requests ({reassignments.length})
          </div>
          <ul className="divide-y divide-violet-100">
            {reassignments.map(r => (
              <li key={r.id} className="px-4 py-2 flex flex-wrap items-center gap-3 text-sm" data-testid={`reassign-item-${r.id}`}>
                <div className="flex-1 min-w-[280px]">
                  <div><b>{r.from_user_name}</b> → <b>{r.to_user_name}</b> · {r.item_name} <span className="text-gray-500">· {r.fund_name}</span></div>
                  {r.message && <div className="text-xs text-gray-600 italic mt-0.5">"{r.message}"</div>}
                </div>
                {r.addressed_to_me ? (
                  <div className="flex gap-2">
                    <button onClick={() => decideReassign(r.id, 'accepted')}
                            className="px-2.5 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700"
                            data-testid={`reassign-accept-${r.id}`}>Accept</button>
                    <button onClick={() => {
                              const note = prompt('Optional note for decline:');
                              if (note !== null) decideReassign(r.id, 'declined', note);
                            }}
                            className="px-2.5 py-1 text-xs border border-red-600 text-red-700 rounded hover:bg-red-50"
                            data-testid={`reassign-decline-${r.id}`}>Decline</button>
                  </div>
                ) : (
                  <span className="text-xs text-gray-500 italic">Pending {r.to_user_name}'s decision</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bulk-assign bar (admin only) */}
      {isAdmin && selected.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="bulk-assign-bar">
          <span className="text-sm text-blue-900 font-medium mr-2" data-testid="bulk-selection-count">{selected.size} selected</span>
          <select value={bulkAssignee} onChange={e => setBulkAssignee(e.target.value)}
                  className="text-sm border rounded px-2 py-1"
                  data-testid="bulk-assignee-select">
            <option value="">Assign to Unassigned</option>
            {team.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button onClick={applyBulkAssign} disabled={bulkSaving}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  data-testid="bulk-assign-apply">
            {bulkSaving ? 'Applying…' : 'Apply'}
          </button>
          <button onClick={() => setSelected(new Set())}
                  className="px-3 py-1 text-sm border rounded hover:bg-gray-100"
                  data-testid="bulk-assign-clear">
            Clear
          </button>
        </div>
      )}

      {view === 'list' ? (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {isAdmin && (
                  <th className="px-3 py-2 w-8">
                    <input type="checkbox"
                           checked={selected.size > 0 && selected.size === occurrences.length}
                           onChange={toggleAll}
                           data-testid="select-all-occ" />
                  </th>
                )}
                <th className="text-left px-4 py-2 font-medium text-gray-700 whitespace-nowrap">Due Date</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700 whitespace-nowrap">Internal Due</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Compliance</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Fund</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Assignee</th>
                {extraCols.map(k => (
                  <th key={k} className="text-left px-4 py-2 font-medium text-gray-700 whitespace-nowrap">
                    {allOptional.find(c => c.key === k)?.label || k}
                  </th>
                ))}
                <th className="text-left px-4 py-2 font-medium text-gray-700">WIP</th>
                <th className="text-left px-4 py-2 font-medium text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {occurrences.length === 0 ? (
                <tr><td colSpan={(isAdmin ? 8 : 7) + extraCols.length} className="px-4 py-8 text-center text-gray-500">
                  {scope === 'mine'
                    ? 'No compliances assigned to you.'
                    : scope === 'unassigned'
                      ? 'Nothing unassigned right now — every item has an owner.'
                      : 'No compliance occurrences yet. Click "Assign to Fund" to attach compliance items to a fund.'}
                </td></tr>
              ) : occurrences.map(o => (
                <tr key={o.id}
                    onClick={(e) => { if (e.target.tagName !== 'INPUT') setDrawer(o); }}
                    className="border-t hover:bg-blue-50 cursor-pointer"
                    data-testid={`occ-row-${o.id}`}>
                  {isAdmin && (
                    <td className="px-3 py-2 w-8" onClick={e => e.stopPropagation()}>
                      <input type="checkbox"
                             checked={selected.has(o.id)}
                             onChange={() => toggleSelected(o.id)}
                             data-testid={`select-occ-${o.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-2 font-mono whitespace-nowrap">{o.due_date ? fmtDate(o.due_date) : <span className="italic text-gray-400 font-sans">event pending</span>}</td>
                  <td className="px-4 py-2 font-mono text-gray-600 whitespace-nowrap">{o.internal_due_date ? fmtDate(o.internal_due_date) : '—'}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900 inline-flex items-center gap-1.5">
                      {o.item_name}
                      {o.is_adhoc && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 font-medium uppercase tracking-wide"
                              data-testid={`adhoc-chip-${o.id}`}
                              title="Fund-specific adhoc task — not in the Compliance Database">
                          Adhoc
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 capitalize">{(o.compliance_type || '').replace('_', '-')}</div>
                  </td>
                  <td className="px-4 py-2 text-gray-700">{o.fund_name}</td>
                  <td className="px-4 py-2">
                    {o.assignee_name
                      ? <span className="text-gray-700">{o.assignee_name}</span>
                      : (
                        <span className="inline-flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded border bg-red-100 text-red-800 border-red-300 font-medium" data-testid={`unassigned-badge-${o.id}`}>Unassigned</span>
                          <button
                            onClick={(e) => { e.stopPropagation(); claimOccurrence(o.id); }}
                            className="text-xs px-2 py-0.5 rounded border border-blue-600 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
                            data-testid={`claim-occ-${o.id}`}
                            title="Assign this compliance to yourself"
                          >
                            Assign to me
                          </button>
                        </span>
                      )}
                  </td>
                  {extraCols.map(k => <td key={k} className="px-4 py-2">{renderExtraCell(o, k)}</td>)}
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${WIP_BADGE[o.wip_status] || ''}`}>{o.wip_status}</span>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`text-xs px-2 py-0.5 rounded border whitespace-nowrap ${STATUS_BADGE[o.compliance_status] || ''}`}>{o.compliance_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <button onClick={() => shiftMonth(-1)} className="p-1 hover:bg-gray-100 rounded" data-testid="cal-prev"><ChevronLeft size={18} /></button>
            <h3 className="font-semibold" data-testid="cal-month-label">
              {new Date(calCursor.y, calCursor.m).toLocaleString('default', { month: 'long', year: 'numeric' })}
            </h3>
            <button onClick={() => shiftMonth(1)} className="p-1 hover:bg-gray-100 rounded" data-testid="cal-next"><ChevronRight size={18} /></button>
          </div>
          <div className="grid grid-cols-7 border-b text-xs font-medium text-gray-500 bg-gray-50">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <div key={d} className="px-2 py-1 text-center">{d}</div>
            ))}
          </div>
          <CalendarGrid year={calCursor.y} month={calCursor.m} items={calItems} openOcc={setDrawer} />
        </div>
      )}

      {drawer && (
        <OccurrenceDrawer
          occurrence={drawer}
          onClose={() => setDrawer(null)}
          onChanged={fetchAll}
          isAdmin={isAdmin}
          currentUserId={user?.id}
          team={team}
        />
      )}

      <AddEventModal
        open={showAddEvent}
        onClose={() => setShowAddEvent(false)}
        funds={funds}
        defaultFundId={filters.fund_id}
        onSaved={fetchAll}
      />

      {showBulkReassign && (
        <BulkReassignModal
          onClose={() => setShowBulkReassign(false)}
          onDone={() => { fetchAll(); fetchReassignments(); }}
        />
      )}

      <AdhocTaskModal
        open={showAdhoc}
        onClose={() => setShowAdhoc(false)}
        defaultFundId={filters.fund_id}
        onSaved={fetchAll}
      />

      {/* Assign-to-Fund picker */}
      {showFundPicker && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="fund-picker-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-semibold">Assign Compliance to a Fund</h3>
              <button onClick={() => setShowFundPicker(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <p className="px-5 pt-3 text-xs text-gray-500">
              Pick a fund to open its assignment matrix — you'll see every compliance line item section-wise and can mark each one applicable (with an assignee) or not-applicable (with a reason).
            </p>
            <div className="px-5 py-3 max-h-80 overflow-y-auto">
              {funds.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No funds in your AMC yet.</p>
              ) : funds.map(f => (
                <button key={f.id}
                        onClick={() => { setShowFundPicker(false); navigate(`/funds/${f.id}/compliance`); }}
                        className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-emerald-50 border border-transparent hover:border-emerald-200 text-sm font-medium text-gray-900 mb-1"
                        data-testid={`fund-pick-${f.id}`}>
                  {f.name}
                </button>
              ))}
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 text-right">
              <button onClick={() => setShowFundPicker(false)} className="px-4 py-2 border rounded text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n)}…` : s);

const CalendarGrid = ({ year, month, items, openOcc }) => {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const numDays = daysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= numDays; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const byDay = {};
  items.forEach(it => {
    const d = Number((it.due_date || '').slice(8, 10));
    if (d) (byDay[d] = byDay[d] || []).push(it);
  });

  return (
    <div className="grid grid-cols-7">
      {cells.map((d, i) => (
        <div key={i} className="min-h-[100px] border-b border-r p-1 text-xs bg-white"
             data-testid={d ? `cal-cell-${year}-${pad2(month + 1)}-${pad2(d)}` : undefined}>
          {d && <div className="text-gray-500 text-[10px] font-medium mb-1">{d}</div>}
          {(byDay[d] || []).map(it => {
            const cls = !it.assignee_name
              ? 'bg-red-100 text-red-800 border-red-300'
              : (STATUS_BADGE[it.compliance_status] || 'bg-gray-100');
            return (
              <button key={it.id} onClick={() => openOcc(it)}
                      className={`w-full text-left mb-1 px-1.5 py-0.5 rounded border truncate ${cls}`}
                      data-testid={`cal-item-${it.id}`}>
                {it.item_name} <span className="opacity-75">· {it.fund_name}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ComplianceTracker;
